/* eslint-disable no-console */
import { useState, useEffect } from "react";
import { Drawer, IconButton, List } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import WcIcon from "@mui/icons-material/Wc";
import {
  School as ClassesIcon,
  ArrowBack as ArrowBackIcon,
  Logout
} from "@mui/icons-material";
import { Icon } from "@iconify/react";
import { useTheme } from "@mui/material/styles";
import classNames from "classnames";
import useStyles from "./styles";
import SidebarLink from "./components/SidebarLink/SidebarLink";
import logo from "../../assets/images/logo.svg";

// --| using context files
import {
  useLayoutState,
  useLayoutDispatch,
  toggleSidebar,
  toggleSidebarOff,
  togglePermamence
} from "../../context/LayoutContext";
import {
  useUserDispatch,
  useUserState,
  logOut
} from "../../context/UserContext";
import { viewLibraryResources, accessViewAllInventoryItems, getCurrentRole, accessAllSchoolPlans, accessSubjectTeacher, accessViewAssignmentById, accessViewAllAccessCodes, accessViewAllLeaveRequests, accessViewAllKnowledgebaseInfo, accessViewClassAttendance, accessCreateGuardian, accessViewAllEmployees, accessCreateStudent, accessAddIntegration, accessGetAllEnrolments, accessViewAllApplications, accessCreateEvent, accessApplyForLeave, accessViewAllAssignmentsInSchool, accessCreateSession, accessCreaeTerm, accessCreateSubject, accessCreateClass } from "../../data/roles";
import { useWard } from "../../context/GaurdianContext";

const Sidebar = () => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole, profile, appLogo, subscription } = useUserState();

  // --| This is to return the role, whether admin or user
  const role = getCurrentRole(userRole);

  // global
  const { isSidebarOpened, isPermanent } = useLayoutState();
  const layoutDispatch = useLayoutDispatch();
  const userDispatch = useUserDispatch();

  const handleWindowWidthChange = () => {
    const windowWidth = window.innerWidth;
    const breakpointWidth = theme.breakpoints.values.md;
    const isSmallScreen = windowWidth < breakpointWidth;

    if (isSmallScreen && isPermanent) {
      togglePermamence(layoutDispatch);
    } else if (!isSmallScreen && !isPermanent) {
      togglePermamence(layoutDispatch);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowWidthChange);
    handleWindowWidthChange();

    return function cleanup() {
      window.removeEventListener("resize", handleWindowWidthChange);
    };
  });

  useEffect(() => {
    toggleSidebarOff(layoutDispatch);
  }, []);

  // --| Re-render the page if role changes
  useEffect(() => {}, [role]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [activeLink, setActiveLink] = useState(location.pathname);

  const handleToggleDropdown = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location.pathname]);
  const { selectedWard } = useWard();

  const subscriptionName = subscription?.pricing_plan?.name;
  const adminStructure = [
    {
      id: 0,
      label: "Onboarding",
      link: "/onboarding-page",
      access: !profile?.onboarding_complete,
      icon: <Icon icon="dashicons:welcome-learn-more" />
    },
    {
      id: 0,
      label: "Dashboard",
      link: "/dashboard",
      access: true,
      icon: <Icon icon="ic:round-dashboard-customize" />
    },
    {
      id: 1,
      label: "Users",
      link: "/users",
      access: true,
      icon: <Icon icon="ph:users-three-bold" />,
      children: [
        { label: "Employees", link: "/users/employees" },
        { label: "Guardian", link: "/users/guardians", icon: <WcIcon /> },
        { label: "Student", link: "/users/students", icon: <ClassesIcon /> }
      ]
    },
    {
      id: 2,
      label: "Class Activities",
      link: "/class-activities",
      access: true,
      icon: <Icon icon="charm:graduate-cap" />,
      children: [
        { label: "Classes", link: "/class-activities/classes" },
        { label: "Subjects", link: "/class-activities/subjects" },
        { label: "Assignments", link: "/assignments" },
        { label: "Attendance", link: "/attendance", access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner"
        },
        { label: "Time Table", link: "/time-table" }
      ]
    },
    {
      id: 50,
      label: "Lesson Note",
      link: "/lesson-notes-dropdown",
      access: true,
      icon: <Icon icon="healthicons:register-book" />,
      children: [
        { label: "Lesson Notes", link: "/lesson-notes" },
        { label: "Lesson Notes Review", link: "/lesson-notes-review", access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner" },
        { label: "Custom Fields", link: "/lesson-notes/custom-fields" }
      ]
    },
    {
      id: 3,
      label: "Continuous Assessment",
      link: "/continous-assessment",
      access: true,
      icon: <Icon icon="simple-icons:testcafe" />,
      children: [
        { label: "CA Tests", link: "/assessment" },
        { label: "CA Results", link: "/assessment-results", icon: <WcIcon /> },
        { label: "CA Audits", link: "/assessment-audits", icon: <ClassesIcon />, access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner"
        }
      ]
    },
    {
      id: 4,
      label: "School Examination",
      link: "/school-examination",
      access: true,
      icon: <Icon icon="ic:outline-assessment" />,
      children: [{ label: "Examinations", link: "/school-examination/examination" },
        { label: "Examination TimeTable", link: "/school-examination/exam-timetable" },
        { label: "Practice Questions", link: "/school-examination/question-bank" },
        { label: "School Question Bank", link: "/school-examination/school-question-bank" }
        // { label: "Broadsheet", link: "/school-examination/broadsheet" },
      ]
    },
    {
      id: 5,
      label: "School Administration",
      link: "/manage",
      access: true,
      icon: <Icon icon="iconoir:hexagon-dice" />,
      children: [
        // { label: "Knowledgebase", link: "/manage/knowledgebase" },
        { label: "Employee Leave", link: "/manage/leave", access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner" },
        { label: "Student Leave", link: "/manage/student-leave", access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner" },
        // { label: "Performance Evaluation", link: "/manage/goals" },
        // { label: "Inventory", link: "/manage/inventory" },
        // { label: "Procurement", link: "/manage/procurements" },
        { label: "Notice Board", link: "/manage/notice-board" },
        {
          id: 11,
          label: "Inventory",
          link: "/inventory",
          access: true,
          icon: <Icon icon="ic:outline-inventory" />
        }
      ]
    },
    {
      id: 6,
      label: "School Plans",
      link: "/schoolplans",
      access: true,
      icon: <Icon icon="lets-icons:calendar" />,
      children: [
        { label: "Terms", link: "/schoolterm" },
        { label: "Sessions", link: "/schoolsession" },
        { label: "Plans", link: "/schoolplans", access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner"
        }
      ]
    },
    {
      id: 7,
      label: "School Enrolments",
      link: "/enrolments",
      access: true,
      icon: <Icon icon="material-symbols:cast-for-education-outline-rounded" />,
      children: [
        { label: "Enrolments", link: "/enrolments" },
        { label: "Applications", link: "/enrolments/applications" },
        { label: "Enrolment Examination", link: "/enrolments/enrolment-examination" }
      ]
    },
    {
      id: 8,
      label: "Access Control",
      link: "/accesscontrol",
      access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner",
      icon: <Icon icon="ic:outline-key-off" />,
      children: [
        { label: "Access Codes", link: "/accesscontrol/codes" },
        { label: "Access Logs", link: "/accesscontrol/logs" },
        { label: "Validate Code", link: "/accesscontrol/validate" }
      ]
    },
    {
      id: 9,
      label: "Payments",
      link: "/payment",
      access: true,
      icon: <Icon icon="hugeicons:payment-02" />,
      children: [
        { label: "Overview", link: "/payment/overview" },
        { label: "Invoice", link: "/payment/invoice", icon: <ClassesIcon /> },
        { label: "Invoice Item", link: "/payment/invoice-item", icon: <WcIcon /> },
        { label: "Discount", link: "/payment/discount", icon: <ClassesIcon /> },
        { label: "Payment History", link: "/payment/payment-history", icon: <ClassesIcon /> },
        // { label: "Transaction History", link: "/payment/transaction-history", icon: <ClassesIcon /> },
        { label: "Student List", link: "/payment/student-list", icon: <ClassesIcon /> },
        { label: "Payment Settings", link: "/payment-settings" }
        // { label: "Offline Payments", link: "/payment/offline-payment", icon: <ClassesIcon /> }
      ]
    },
    {
      id: 18,
      label: "Report Management",
      link: "/report",
      access: true,
      icon: <Icon icon="bx:file" />,
      children: [
        { label: "Obtainable Score", link: "/obtainable-score", icon: <ClassesIcon /> },
        { label: "Grading System", link: "/report/grade-system" },
        { label: "Broadsheet", link: "/report/broadsheet" },
        { label: "Term Reports", link: "/report/reports" },
        { label: "Session Reports", link: "/report/session" },
        { label: "Custom Reports", link: "/report/custom", access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner"
        },
        { label: "Promotion", link: "/report/promotion" },
        { label: "Report Template", link: "/report/template" }
      ]
    },
    {
      id: 14,
      label: "Mailing",
      link: "/messaging",
      access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner",
      icon: <Icon icon="lucide:mail" />,
      children: [
        { label: "Email Subscription", link: "/messaging/subcription" },
        { label: "Subscriber", link: "/messaging/subcriber" },
        { label: "Email Template", link: "/messaging/template" },
        { label: "Send Bulk Emails", link: "/messaging/bulkmails" },
        { label: "Mailing Settings", link: "/messaging/mailing-settings" }
      ]
    },
    // { id: 18, label: "Access Control", link: "/accesscontrol", icon: <Icon icon="ic:outline-key-off" /> },
    {
      id: 10,
      label: "Library",
      link: "/library",
      access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner",
      icon: <Icon icon="ic:outline-video-library" />
    },
    // {
    //   id: 10,
    //   label: "Audit Trail",
    //   link: "/audit-trail",
    //   access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner",
    //   icon: <Icon icon="fluent:my-location-16-regular" />
    // },
    {
      id: 10,
      label: "Hostel Management",
      link: "/hostel",
      access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner",
      icon: <Icon icon="lsicon:building-outline" />
    },
    // {
    //   id: 11,
    //   label: "Payment Settings",
    //   link: "/payment-settings",
    //   access: true,
    //   icon: <Icon icon="mingcute:plugin-2-line" />
    // },
    {
      id: 12,
      label: "Event",
      link: "/events",
      access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner",
      icon: <Icon icon="lets-icons:calendar" />
    },
    // {
    //   id: 14,
    //   label: "Configuration",
    //   link: "/configuration",
    //   access: true,
    //   icon: <Icon icon="eos-icons:configuration-file-outlined" />
    // },
    {
      id: 13,
      label: "Settings",
      link: "/settings",
      access: true,
      icon: <Icon icon="tabler:settings" />
    },
    // {
    //   id: 40,
    //   label: "Trash",
    //   link: "/trash",
    //   access: subscriptionName === "Pro Plan" || subscriptionName === "Partner",
    //   icon: <Icon icon="basil:trash-alt-outline" />
    // },
    {
      id: 36,
      label: "Support",
      link: "/support/tickets",
      access: true,
      icon: <Icon icon="fluent:person-support-32-regular" />
    }
  ];

  const userStructure = [
    {
      id: 14,
      label: "Dashboard",
      link: "/dashboard",
      icon: <Icon icon="ic:round-dashboard-customize" />
    },
    {
      id: 15,
      label: "School Plans",
      link: "/schoolplans",
      access: accessAllSchoolPlans(userRole),
      icon: <Icon icon="lets-icons:calendar" />
    },
    {
      id: 16,
      label: "Examination",
      link: "/examinations",
      icon: <Icon icon="fa6-solid:list-check" />,
      children: [{ label: "Examination", link: "/examinations" },
        { label: "Examination TimeTable", link: "/school-examination/exam-timetable" }
        // { label: "Broadsheet", link: "/school-examination/broadsheet" },
      ]
    },
    // { id: 21, label: "Assignment", link: "/assignments", icon: <Icon icon="ic:round-dashboard-customize" /> },
    // { id: 6, label: "Performance Evaluation", link: "/manage/goals", icon: <Icon icon="material-symbols:readiness-score-outline" /> },
    {
      id: 17,
      label: "Classes",
      access: true,
      link: "/myclasses",
      icon: <Icon icon="charm:graduate-cap" />
    },
    {
      id: 18,
      label: "Library",
      link: "/library",
      access: viewLibraryResources(userRole),
      icon: <Icon icon="ic:outline-video-library" />
    },
    {
      id: 19,
      label: "Assessments",
      link: "/assesments",
      access: accessViewAssignmentById(userRole),
      icon: <Icon icon="ic:outline-assessment" />,
      children: [
        { label: "Assignments", link: "/my-assignments" },
        { label: "CA Tests", link: "/assessment" },
        { label: "CA Results", link: "/assessment-results", icon: <WcIcon /> },
        { label: "CA Audits", link: "/assessment-audits", icon: <ClassesIcon /> }
      ]
    },
    {
      id: 28,
      label: "School Administration",
      link: "/manage",
      icon: <Icon icon="iconoir:hexagon-dice" />,
      access:
      accessViewAllKnowledgebaseInfo(userRole) ||
      accessViewAllLeaveRequests(userRole) ||
      accessViewAllEmployees(userRole) ||
      accessCreateGuardian(userRole) ||
      accessCreateStudent(userRole) ||
      accessAddIntegration(userRole) ||
      accessGetAllEnrolments(userRole) ||
      accessViewAllApplications(userRole) ||
      accessCreateClass(userRole) ||
      accessViewAllAssignmentsInSchool(userRole) ||
      accessCreateSubject(userRole),
      children: [
        { label: "Employees", link: "/users/employees", access: accessViewAllEmployees(userRole) },
        { label: "Guardian", link: "/users/guardians", icon: <WcIcon />, access: accessCreateGuardian(userRole) },
        { label: "Student", link: "/users/students", icon: <ClassesIcon />, access: accessCreateStudent(userRole) },
        { label: "Knowledgebase", link: "/manage/knowledgebase", access: accessViewAllKnowledgebaseInfo(userRole) },
        { label: "Leave Management", link: "/manage/leave", access: accessViewAllLeaveRequests(userRole) },
        {
          label: "Integrations",
          link: "/integrations",
          access: accessAddIntegration(userRole),
          icon: <Icon icon="mingcute:plugin-2-line" />
        },
        {
          label: "Inventory",
          link: "/inventory",
          access: accessViewAllInventoryItems(userRole)
        },
        { label: "Enrolments", link: "/enrolments", access: accessGetAllEnrolments(userRole) },
        { label: "Applications", link: "/enrolments/applications", access: accessViewAllApplications(userRole) },
        { label: "Classes", link: "/class-activities/classes", access: accessCreateClass(userRole) },
        { label: "Subjects", link: "/class-activities/subjects", access: accessCreateSubject(userRole) },
        { label: "School Assignments", link: "/assignments", access: accessViewAllAssignmentsInSchool(userRole) }
      ]
    },
    // {
    //   id: 41,
    //   label: "Student Leave",
    //   access: true,
    //   link: "/manage/student-leave",
    //   icon: <Icon icon="pepicons-pop:leave-circle" />
    // },
    {
      id: 20,
      label: "Subjects",
      link: "/mysubjects",
      access: accessSubjectTeacher(userRole),
      icon: <Icon icon="streamline:quality-education" />
    },
    {
      id: 42,
      label: "Hostel",
      link: "/hostel",
      access: userRole?.is_hostel_master,
      // access: true,
      icon: <Icon icon="lsicon:building-outline" />
    },
    {
      id: 40,
      label: "Time Table",
      link: "/time-table",
      access: true,
      icon: <Icon icon="tdesign:task-time" />
    },
    {
      id: 21,
      label: "Lesson Note",
      link: "/lesson-notes-dropdown",
      access: true,
      icon: <Icon icon="healthicons:register-book" />,
      children: [
        { label: "Lesson Notes", link: "/lesson-notes" },
        { label: "Lesson Notes Review", link: "/lesson-notes-review", access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner" }
      ]
    },
    {
      id: 22,
      label: "Attendance",
      link: "/attendance",
      access: accessViewClassAttendance(userRole),
      icon: <Icon icon="carbon:task-location" />
    },
    {
      id: 18,
      label: "Report Management",
      link: "/reports",
      access: true,
      icon: <Icon icon="bx:file" />,
      children: [
        { label: "Academic Broadsheet", link: "/reports/broadsheet" },
        { label: "Reports", link: "/reports/reports" }
      ]
    },
    {
      id: 43,
      label: "Welfare Management",
      link: "/welfare",
      access: accessApplyForLeave(userRole),
      icon: <Icon icon="material-symbols:favorite-outline" />,
      children: [
        { label: "My Welfare", link: "/welfare", access: accessApplyForLeave(userRole) },
        { label: "Student Leave", link: "/manage/student-leave", access: true }
      ]
    },
    { id: 23, label: "Notice Board", link: "/employee/notice-board", icon: <Icon icon="mdi:bell-outline" /> },
    // { id: 9, label: "Inventory", link: "/manage/inventory", icon: <Icon icon="mdi:shield-check-outline" /> },
    // { id: 10, label: "Procurement", link: "/manage/procurements", icon: <Icon icon="uil:shopping-cart" /> },
    // {
    //   id: 11,
    //   label: "Resources",
    //   access: accessViewAllKnowledgebaseInfo(userRole) || true,
    //   link: "/manage/knowledgebase",
    //   icon: <Icon icon="grommet-icons:resources" />
    // },
    // {
    //   id: 24,
    //   label: "Welfare",
    //   link: "/welfare",
    //   access: accessApplyForLeave(userRole),
    //   icon: <Icon icon="material-symbols:favorite-outline" />
    // },
    {
      id: 25,
      label: "Access Control",
      link: "/accesscontrol",
      access: accessViewAllAccessCodes(userRole),
      icon: <Icon icon="ic:outline-key-off" />,
      children: [
        { label: "Access Codes", link: "/accesscontrol/codes" },
        { label: "Access Logs", link: "/accesscontrol/logs" },
        { label: "Validate Code", link: "/accesscontrol/validate" }
      ]
    },
    {
      id: 26,
      label: "Event",
      link: "/events",
      access: accessCreateEvent(userRole),
      icon: <Icon icon="lets-icons:calendar" />
    },
    {
      id: 44,
      label: "Notifications",
      link: "/notifications",
      access: true,
      icon: <Icon icon="iconamoon:notification-bold" />
    },
    {
      id: 99,
      label: "Wallet",
      link: "/wallet",
      icon: <Icon icon="ph:wallet-bold" />
    },
    {
      id: 27,
      label: "Settings",
      link: "/settings",
      icon: <Icon icon="tabler:settings" />
    },
    {
      id: 39,
      label: "School Plans",
      link: "/schoolplans",
      access: accessCreaeTerm(userRole) || accessCreateSession(userRole),
      icon: <Icon icon="lets-icons:calendar" />,
      children: [
        { label: "Terms", link: "/schoolterm", access: accessCreaeTerm(userRole) },
        { label: "Sessions", link: "/schoolsession", access: accessCreateSession(userRole) }
      ]
    },

    {
      id: 36,
      label: "Support",
      link: "/support/tickets",
      access: true,
      icon: <Icon icon="fluent:person-support-32-regular" />
    }
  ];

  const studentStructure = [
    {
      id: 29,
      label: "Dashboard",
      link: "/dashboard",
      icon: <Icon icon="ic:round-dashboard-customize" />
    },
    {
      id: 30,
      label: "Class Activities",
      link: "/class-activities",
      icon: <Icon icon="charm:graduate-cap" />,
      children: [
        // { label: "Syllabus", link: "/class-activities/syllabus" },
        { label: "Subjects", link: "/class-activities/subjects" },
        { label: "Attendance", link: "/attendance" },
        { label: "Time Table", link: "/time-table" }
      ]
    },
    {
      id: 31,
      label: "Assessments",
      link: "/assesments",
      icon: <Icon icon="ic:outline-assessment" />,
      children: [
        { label: "Assignments", link: "/assesments/assignments" },
        { label: "Examinations", link: "/assesments/student-examination" },
        { label: "Examination TimeTable", link: "/school-examination/exam-timetable" },
        { label: "Test", link: "/assesments/student-test" },
        { label: "Test Results", link: "/assesments/student-test-result" },
        { label: "Grades", link: "/assesments/student/grades" }
      ]
    },
    {
      id: 32,
      label: "School Plans",
      link: "/schoolplans",
      icon: <Icon icon="lets-icons:calendar" />
    },
    { label: "Notice Board", link: "/student/notice-board", icon: <Icon icon="mdi:bell-outline" /> },
    {
      id: 33,
      label: "Library",
      link: "/library",
      icon: <Icon icon="ic:outline-video-library" />
    },
    {
      id: 100,
      label: "My Learning",
      link: "/student/my-learning",
      icon: <Icon icon="fluent:learning-app-24-regular" />
    },
    {
      id: 20,
      label: "Payments",
      link: "/payment/overview",
      access: true,
      icon: <Icon icon="hugeicons:payment-02" />
    },
    {
      id: 99,
      label: "Rewards",
      link: "/student/reward",
      icon: <Icon icon="ph:gift-bold" />
    },
    {
      id: 42,
      label: "Hostel",
      link: "/hostel",
      icon: <Icon icon="lsicon:building-outline" />
    },
    {
      id: 35,
      label: "Event",
      link: "/events",
      icon: <Icon icon="lets-icons:calendar" />
    },
    {
      id: 44,
      label: "Notifications",
      link: "/notifications",
      access: true,
      icon: <Icon icon="iconamoon:notification-bold" />
    },
    {
      id: 34,
      label: "Settings",
      link: "/settings",
      icon: <Icon icon="tabler:settings" />
    }
    // {
    //   id: 36,
    //   label: "Support",
    //   link: "/support/tickets",
    //   access: true,
    //   icon: <Icon icon="fluent:person-support-32-regular" />
    // }
  ];

  const guardianStructure = [
    {
      id: 29,
      label: "Dashboard",
      link: "/dashboard",
      icon: <Icon icon="ic:round-dashboard-customize" />
    },
    {
      id: 30,
      label: "Class Activities",
      link: "/class-activities",
      access: !!selectedWard,
      icon: <Icon icon="charm:graduate-cap" />,
      children: [
        // { label: "Syllabus", link: "/class-activities/syllabus" },
        { label: "Subjects", link: "/guardian/Subject" },
        { label: "Attendance", link: "/guardian/attendace" },
        { label: "Time Table", link: "/guardian/timetable" }
      ]
    },
    {
      id: 31,
      label: "Assessments",
      link: "/assesments",
      access: !!selectedWard,
      icon: <Icon icon="ic:outline-assessment" />,
      children: [
        { label: "Assignments", link: "/guardian/assignments" },
        { label: "Grades", link: "/guardian/student/grades" }
      ]
    },
    { label: "Notice Board", link: "/guardian/notice-board", icon: <Icon icon="mdi:bell-outline" /> },
    {
      id: 20,
      label: "Payments",
      access: !!selectedWard,
      link: "/guardian/payment",
      icon: <Icon icon="hugeicons:payment-02" />,
      children: [
        { label: "Overview", link: "/guardian/payment" },
        { label: "Transaction History", link: "/guardian/transaction" }
      ]
    },
    {
      id: 35,
      label: "Event",
      link: "/events",
      icon: <Icon icon="lets-icons:calendar" />,
      access: subscriptionName === "Pro" || subscriptionName === "Enterprise" || subscriptionName === "Partner"
    },
    {
      id: 44,
      label: "Notifications",
      link: "/notifications",
      access: true,
      icon: <Icon icon="iconamoon:notification-bold" />
    },
    {
      id: 34,
      label: "Settings",
      link: "/settings",
      icon: <Icon icon="tabler:settings" />
    }

    // {
    //   id: 36,
    //   label: "Support",
    //   link: "/support/tickets",
    //   access: true,
    //   icon: <Icon icon="fluent:person-support-32-regular" />
    // }
  ];
  // const suportStructure = [
  //   {
  //     id: 36,
  //     label: "Support",
  //     link: "/support/tickets",
  //     access: true,
  //     icon: <Icon icon="mdi:love" />
  //     // children: [
  //     //   { label: "Tickets", link: "/support/tickets" }
  //     //   // { label: "Open Ticket", link: "/support/open-ticket", icon: <WcIcon /> }
  //     // ]
  //   }
  // ];

  const structure =
    role === "admin"
      ? adminStructure
      : role === "employee"
        ? userStructure
        : role === "student"
          ? studentStructure
          : role === "guardian"
            ? guardianStructure
            : [];

  const Initial = profile?.first_name?.split("")[0].toUpperCase();

  return (
    <Drawer
      variant={isPermanent ? "permanent" : "temporary"}
      className={classNames(classes.drawer, {
        [classes.drawerOpen]: isSidebarOpened,
        [classes.drawerClose]: !isSidebarOpened
      })}
      classes={{
        paper: classNames({
          [classes.drawerOpen]: isSidebarOpened,
          [classes.drawerClose]: !isSidebarOpened
        })
      }}
      open={isSidebarOpened}
      onClose={() => toggleSidebar(layoutDispatch)}
    >
      <div className={classes.sidebarLogo}>
        <Link to="/dashboard">
          <div className={classes.logoContent}>
            <img
              src={appLogo || logo}
              alt="App Logo"
              className={classes.logoContent}
            />
          </div>
        </Link>
      </div>
      <div className={classes.mobileBackButton}>
        <IconButton onClick={() => toggleSidebar(layoutDispatch)}>
          <ArrowBackIcon
            classes={{
              root: classNames(classes.headerIcon, classes.headerIconCollapse)
            }}
          />
        </IconButton>
      </div>
      <List className={classes.sidebarList}>
        {structure.map((link) => (
          <SidebarLink
            key={link.id}
            location={location}
            isSidebarOpened={isSidebarOpened}
            openDropdown={openDropdown}
            handleToggleDropdown={handleToggleDropdown}
            activeLink={activeLink}
            setActiveLink={setActiveLink}
            {...link}
          />
        ))}
      </List>
      {/* <div className={classes.support}>
        <List className={classes.supportsidebarList}>
          {suportStructure.map((link) => (
            <SidebarLink
              key={link.id}
              location={location}
              isSidebarOpened={isSidebarOpened}
              openDropdown={openDropdown}
              handleToggleDropdown={handleToggleDropdown}
              activeLink={activeLink}
              setActiveLink={setActiveLink}
              {...link}
            />
          ))}
        </List>
      </div> */}
      <div className={classes.footer}>
        <Link className={classes.footerlink} to="/profile">
          <div className={classes.footercontainer}>
            <div className={classes.initials}>{Initial}</div>
            <div className={classes.footertextwrapper}>
              <p className={classes.name}>
                {`${profile?.first_name} ${profile?.last_name}`}
              </p>
              <p className={classes.email}>{profile?.email}</p>
            </div>
          </div>
        </Link>
        <div
          className={classes.logout}
          onClick={() => logOut(userDispatch, navigate)}
        >
          <Logout />
        </div>
      </div>
    </Drawer>
  );
};

export default Sidebar;
